"use client";

import { motion } from "framer-motion";
import { CheckCircle2, Circle, ClipboardList, Info } from "lucide-react";
import { useState } from "react";

import { DocumentPreviewModal } from "@/components/app/documents/document-preview-modal";
import { DocumentSelectorDialog } from "@/components/app/documents/document-selector-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useJourneyData } from "@/hooks/use-journey-data";
import { downloadFile } from "@/lib/download-file";
import { uploadFile } from "@/lib/storage";
import type {
  Document,
  DocumentCategory,
  uploadSchema,
} from "@/schemas/document";
import type { z } from "zod";

interface ClosingDocumentConfig {
  label: string;
  description: string;
  tooltip: string;
  category: DocumentCategory;
  type: string;
}

const CLOSING_DOCUMENTS: Record<string, ClosingDocumentConfig> = {
  wire_transfer_receipt: {
    label: "Wire Transfer Receipt",
    description:
      "Proof of funds transfer for your closing costs and down payment",
    tooltip:
      "This document confirms you've sent the required funds to close on your home",
    category: "closing",
    type: "wire_transfer_receipt",
  },
  final_closing_disclosure: {
    label: "Final Closing Disclosure",
    description:
      "The final itemized list of all costs and credits for your purchase",
    tooltip:
      "This document breaks down exactly what you're paying for and receiving in the transaction",
    category: "closing",
    type: "closing_disclosure",
  },
  closing_ids: {
    label: "Government Photo ID",
    description:
      "Valid government-issued photo identification (driver's license or passport)",
    tooltip:
      "Required to verify your identity at closing - must be unexpired and government-issued",
    category: "closing",
    type: "identification_document",
  },
} as const;

const CLOSING_TASKS = {
  wire_closing_funds: {
    label: "Wire Closing Funds",
    description: "Send required funds to the title company",
    tooltip: "Track when you've sent the closing funds to the title company",
  },
  sign_closing_docs: {
    label: "Sign Closing Documents",
    description: "Review and sign all closing paperwork",
    tooltip: "Track when you've completed signing all closing documents",
  },
} as const;

export function ClosingPreparationChecklist() {
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(
    null,
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeDocRef, setActiveDocRef] = useState<
    keyof typeof CLOSING_DOCUMENTS | null
  >(null);
  const [previewDocument, setPreviewDocument] = useState<Document | null>(null);

  const {
    documents,
    journeyDocuments,
    tasks,
    journeyTasks,
    createDocument,
    addBuyerDocument,
    createTask,
    addBuyerTask,
    toggleTaskComplete,
    journeyData,
    userProfile,
  } = useJourneyData();

  const handleUpload = async (formData: z.infer<typeof uploadSchema>) => {
    try {
      if (!userProfile?.id) {
        throw new Error("No user ID found");
      }

      const fileName = `${userProfile?.id}/documents/${Date.now()}_${formData.file.name}`;
      const { url, path } = await uploadFile(
        "documents",
        fileName,
        formData.file,
      );

      // Create document with reference_id for optimistic updates
      await createDocument.mutateAsync({
        type: formData.type,
        category: formData.category,
        name: formData.file.name,
        url,
        file_type: formData.file.type,
        file_path: path,
        size_bytes: formData.file.size,
        reference_id: activeDocRef || undefined,
      });

      // No need to create buyer-document relationship separately
      // as it's handled optimistically in createDocument mutation
    } catch (error) {
      console.error("Upload failed:", error);
      throw error;
    }
  };

  const handleTaskAction = async (ref: string) => {
    const existingJourneyTask = journeyTasks.find(
      (bt) => bt.reference_id === ref,
    );
    const existingTask = existingJourneyTask
      ? tasks.find((t) => t.id === existingJourneyTask.task_id)
      : null;
    const taskConfig = CLOSING_TASKS[ref as keyof typeof CLOSING_TASKS];

    if (!existingTask) {
      try {
        if (!userProfile?.id) throw new Error("No user ID found");

        // Create the task with reference_id
        const newTask = await createTask.mutateAsync({
          title: taskConfig.label,
          category: "other",
          priority: "high",
          due_date: null,
          reference_id: ref, // Pass the reference_id here
        });

        // Create the buyer-task relationship
        await addBuyerTask.mutateAsync({
          task_id: newTask.id,
          reference_id: ref,
          journey_id: journeyData?.id ?? "",
          user_id: userProfile?.id ?? "",
        });
      } catch (error) {
        console.error("Failed to create task:", error);
      }
    } else {
      await toggleTaskComplete.mutateAsync({
        id: existingTask.id,
        completed: !existingTask.completed,
      });
    }
  };

  const getDocumentForRef = (ref: string) => {
    const journeyDoc = journeyDocuments.find((bd) => bd.reference_id === ref);
    if (!journeyDoc) return null;
    return documents.find((d) => d.id === journeyDoc.document_id);
  };

  const getTaskForRef = (ref: string) => {
    const journeyTaskId = journeyTasks.find(
      (bt) => bt.reference_id === ref,
    )?.task_id;

    // Handle both real and optimistic tasks
    if (!journeyTaskId) return null;
    return tasks.find(
      (t) =>
        t.id === journeyTaskId ||
        (t.id.startsWith("temp-") && journeyTaskId.startsWith("temp-")),
    );
  };

  const completedCount =
    Object.keys(CLOSING_DOCUMENTS).filter((ref) => getDocumentForRef(ref))
      .length +
    Object.keys(CLOSING_TASKS).filter((ref) => getTaskForRef(ref)?.completed)
      .length;

  const totalCount =
    Object.keys(CLOSING_DOCUMENTS).length + Object.keys(CLOSING_TASKS).length;
  const progress = (completedCount / totalCount) * 100;

  const canPreviewFile = (fileType: string) => {
    return fileType.startsWith("image/") || fileType === "application/pdf";
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="space-y-2">
        <div className="flex flex-col md:flex-row gap-2 items-center justify-between">
          <h3 className="text-lg font-semibold">
            Closing Preparation Progress
          </h3>
          <Badge variant="outline" className="text-sm">
            {completedCount} of {totalCount} Complete
          </Badge>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <div className="space-y-6">
        <div className="space-y-4">
          {Object.entries(CLOSING_DOCUMENTS).map(([ref, config]) => (
            <Card key={ref} className="p-4">
              <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold">{config.label}</h4>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-4 w-4 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent>{config.tooltip}</TooltipContent>
                    </Tooltip>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {config.description}
                  </p>
                </div>

                <div className="ml-auto">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setActiveDocRef(ref as keyof typeof CLOSING_DOCUMENTS);
                      setIsDialogOpen(true);
                    }}
                  >
                    Add Document
                  </Button>
                </div>
              </div>

              {getDocumentForRef(ref) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-4 pt-4 border-t"
                >
                  <div className="flex items-center gap-4">
                    <ClipboardList className="h-5 w-5 text-muted-foreground" />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">
                        {getDocumentForRef(ref)?.name ?? ""}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Added{" "}
                        {new Date(
                          getDocumentForRef(ref)?.created_at ?? "",
                        ).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {canPreviewFile(
                        getDocumentForRef(ref)?.file_type ?? "",
                      ) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setPreviewDocument(getDocumentForRef(ref) ?? null)
                          }
                        >
                          Preview
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const doc = getDocumentForRef(ref);
                          if (doc) downloadFile(doc.url, doc.name);
                        }}
                      >
                        Download
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}
            </Card>
          ))}
        </div>

        <div className="space-y-4">
          {Object.entries(CLOSING_TASKS).map(([ref, config]) => {
            const task = getTaskForRef(ref);
            const isCompleted = task?.completed;
            const isTracking = !!task;

            return (
              <Card key={ref} className="p-4">
                <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold">{config.label}</h4>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-4 w-4 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>{config.tooltip}</TooltipContent>
                      </Tooltip>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {config.description}
                    </p>
                  </div>

                  <div className="ml-auto">
                    {!isTracking ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleTaskAction(ref)}
                        className="gap-2"
                      >
                        <ClipboardList className="h-4 w-4" />
                        Track Task
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleTaskAction(ref)}
                        className="gap-2"
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-5 w-5 text-primary" />
                        ) : (
                          <Circle className="h-5 w-5 text-muted-foreground" />
                        )}
                        {isCompleted ? "Completed" : "Mark Complete"}
                      </Button>
                    )}
                  </div>
                </div>

                {isTracking && isCompleted && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-3"
                  >
                    <p className="text-sm text-primary">Task completed</p>
                  </motion.div>
                )}
              </Card>
            );
          })}
        </div>
      </div>

      <DocumentSelectorDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title={activeDocRef ? CLOSING_DOCUMENTS[activeDocRef].label : ""}
        description={
          activeDocRef ? CLOSING_DOCUMENTS[activeDocRef].description : ""
        }
        documents={documents}
        selectedDocument={selectedDocument}
        onSelect={(doc) => {
          if (doc && activeDocRef) {
            addBuyerDocument.mutate({
              document_id: doc.id,
              reference_id: activeDocRef,
              journey_id: journeyData?.id ?? "",
              user_id: userProfile?.id ?? "",
            });
          }
          setIsDialogOpen(false);
        }}
        onUpload={handleUpload}
        category={
          activeDocRef ? CLOSING_DOCUMENTS[activeDocRef].category : "closing"
        }
        type={activeDocRef ? CLOSING_DOCUMENTS[activeDocRef].type : "other"}
      />

      <DocumentPreviewModal
        document={previewDocument}
        onOpenChange={(open) => !open && setPreviewDocument(null)}
      />
    </div>
  );
}
